import { Injectable } from '@angular/core';
import type { RecordingSection } from './audio-recorder';

/**
 * Where a recording waits until something reads it.
 *
 * A recording is a large binary that belongs to one device and is never synced, so
 * it is held in IndexedDB, outside the databases whose contents are replicated.
 *
 * Keyed by the note id, so a recording and the note it belongs to find each other
 * without a second index, and an orphan is recognisable by having no note.
 */
export interface HeldRecording {
  noteId: string;
  audio: Blob;
  mimeType: string;
  /** When the recording was made, for age-based clean-up. */
  recordedAt: number;
  /** Seconds of audio, where the recorder reported it. */
  seconds: number | null;
  /** The byte length of each file `audio` is made of, where it is made of more than one. */
  partLengths?: number[];
  /** The type of each file `audio` is made of, in the same order as `partLengths`. */
  partTypes?: string[];
  /** Where the words of each run of files belong in the note. Absent for a recording whose words all go at the end. */
  sections?: RecordingSection[];
}

const DB_NAME = 'voice-note-recordings';
const STORE = 'recordings';
const DB_VERSION = 1;

@Injectable({ providedIn: 'root' })
export class RecordingStore {
  #db: Promise<IDBDatabase> | null = null;

  /** Whether this platform can hold a recording at all. */
  public get isAvailable(): boolean {
    return typeof indexedDB !== 'undefined';
  }

  public async put(recording: HeldRecording): Promise<void> {
    await this.#run('readwrite', (store) => store.put(recording, recording.noteId));
  }

  public async get(noteId: string): Promise<HeldRecording | null> {
    return (await this.#run<HeldRecording | undefined>('readonly', (store) => store.get(noteId))) ?? null;
  }

  /** Every recording held, so the sweep can find work without asking the note store first. */
  public async heldNoteIds(): Promise<string[]> {
    const keys = await this.#run<IDBValidKey[]>('readonly', (store) => store.getAllKeys());
    return (keys ?? []).map(String);
  }

  public async remove(noteId: string): Promise<void> {
    await this.#run('readwrite', (store) => store.delete(noteId));
  }

  /** Everything, for sign-out. */
  public async clear(): Promise<void> {
    await this.#run('readwrite', (store) => store.clear());
  }

  #open(): Promise<IDBDatabase> {
    this.#db ??= new Promise<IDBDatabase>((resolve, reject) => {
      const request = indexedDB.open(DB_NAME, DB_VERSION);
      request.onupgradeneeded = () => {
        if (!request.result.objectStoreNames.contains(STORE)) {
          request.result.createObjectStore(STORE);
        }
      };
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
    return this.#db;
  }

  async #run<T>(mode: IDBTransactionMode, action: (store: IDBObjectStore) => IDBRequest): Promise<T | undefined> {
    if (!this.isAvailable) {
      return undefined;
    }
    const db = await this.#open();
    return new Promise<T | undefined>((resolve, reject) => {
      const transaction = db.transaction(STORE, mode);
      const request = action(transaction.objectStore(STORE));
      request.onsuccess = () => resolve(request.result as T);
      request.onerror = () => reject(request.error);
    });
  }
}
